import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { createProjectSchema, type CreateProjectInput } from '@/schemas/organization'
import { organizationService } from '@/services/organization'
import { useOrganizationStore } from '@/stores/organization-store'

const COLOR_PRESETS = [
  { value: '#3B82F6', name: 'Blue' },
  { value: '#10B981', name: 'Emerald' },
  { value: '#8B5CF6', name: 'Violet' },
  { value: '#F59E0B', name: 'Amber' },
  { value: '#EF4444', name: 'Red' },
  { value: '#EC4899', name: 'Pink' },
  { value: '#06B6D4', name: 'Cyan' },
  { value: '#84CC16', name: 'Lime' },
]

const EMOJI_OPTIONS = ['📁', '🚀', '💡', '🎯', '⚡', '🔧', '📊', '🤖']

interface CreateProjectDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CreateProjectDialog({ open, onOpenChange }: CreateProjectDialogProps) {
  const { t } = useTranslation('organizations')
  const { currentOrganization, addProject, setCurrentProject } = useOrganizationStore()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<CreateProjectInput>({
    resolver: zodResolver(createProjectSchema),
    defaultValues: {
      name: '',
      description: '',
      icon: '',
      color: '',
    },
    mode: 'onChange',
  })

  const selectedColor = watch('color')
  const selectedIcon = watch('icon')

  const handleClose = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    reset()
    setError(null)
    setSuccess(false)
    onOpenChange(false)
  }, [reset, onOpenChange])

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const onSubmit = async (data: CreateProjectInput) => {
    if (!currentOrganization) return

    setIsSubmitting(true)
    setError(null)

    try {
      const response = await organizationService.createProject(currentOrganization.id, data)

      addProject(response.project)
      setCurrentProject(response.project)
      setSuccess(true)

      timeoutRef.current = setTimeout(() => {
        handleClose()
      }, 1000)
    } catch (err: unknown) {
      const axiosError = err as { response?: { status?: number; data?: { detail?: string } } }
      if (axiosError.response?.status === 409) {
        setError(t('project.create.errorDuplicate'))
      } else {
        setError(t('project.create.errorMessage'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSelectIcon = (emoji: string) => {
    setValue('icon', selectedIcon === emoji ? '' : emoji, { shouldValidate: true })
  }

  const handleSelectColor = (color: string) => {
    setValue('color', selectedColor === color ? '' : color, { shouldValidate: true })
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{t('project.create.title')}</DialogTitle>
          <DialogDescription>{t('project.create.description')}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Name field */}
          <div className="space-y-2">
            <Label htmlFor="project-name">{t('project.create.nameLabel')}</Label>
            <Input
              id="project-name"
              placeholder={t('project.create.namePlaceholder')}
              {...register('name')}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          {/* Description field */}
          <div className="space-y-2">
            <Label htmlFor="project-description">{t('project.create.descriptionLabel')}</Label>
            <Textarea
              id="project-description"
              placeholder={t('project.create.descriptionPlaceholder')}
              {...register('description')}
              disabled={isSubmitting}
              rows={3}
            />
          </div>

          {/* Icon selection */}
          <div className="space-y-2">
            <Label>{t('project.create.iconLabel')}</Label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('project.create.iconLabel')}>
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  role="radio"
                  aria-checked={selectedIcon === emoji}
                  aria-label={emoji}
                  onClick={() => handleSelectIcon(emoji)}
                  disabled={isSubmitting}
                  className={`flex h-9 w-9 items-center justify-center rounded-md border text-lg transition-colors ${
                    selectedIcon === emoji
                      ? 'border-primary bg-primary/10'
                      : 'border-input hover:bg-accent'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          {/* Color selection */}
          <div className="space-y-2">
            <Label>{t('project.create.colorLabel')}</Label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('project.create.colorLabel')}>
              {COLOR_PRESETS.map(({ value, name }) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={selectedColor === value}
                  aria-label={name}
                  onClick={() => handleSelectColor(value)}
                  disabled={isSubmitting}
                  className={`h-8 w-8 rounded-full border-2 transition-all ${
                    selectedColor === value
                      ? 'border-primary scale-110'
                      : 'border-transparent hover:scale-105'
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                  style={{ backgroundColor: value }}
                />
              ))}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}

          {/* Success message */}
          {success && (
            <p className="text-sm text-emerald-500" role="status">
              {t('project.create.successMessage')}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              {t('project.create.cancel')}
            </Button>
            <Button type="submit" disabled={!isValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('project.create.submitting')}
                </>
              ) : (
                t('project.create.submit')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
